import React from "react";

import { faEllipsis } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Chip, DropDownMenu, Icon, Skeleton, Table, Text, Tooltip } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type {
	CompetencyAssignmentJSON,
	CompetencyAssignmentUserDict,
	CompetencyDict,
} from "@performyard/service-contracts";

import type { SearchServiceSort } from "../../../../api/hooks";

import { resolveLevelName } from "../../utils";

// The py-components Table wrapper seeds `loading=true` with empty-object rows and relies on
// its default cellRendererSelector to swap in a Skeleton — that selector is bypassed when
// a column supplies its own cellRenderer, so each custom cellRenderer has to opt back in.
const isLoadingRow = (data: Partial<CompetencyAssignmentJSON>) => !data._id;

type CompetenciesTableProps = {
	assignments: CompetencyAssignmentJSON[];
	competencies: CompetencyDict;
	/** When supplied, splices Assignee + Role columns after Competency (Team + Company layouts). */
	users?: CompetencyAssignmentUserDict;
	loading: boolean;
	canUpdateCurrentLevel: boolean;
	sort: SearchServiceSort;
	onSortChanged: (e: SortChangedEvent<CompetencyAssignmentJSON>) => void;
	onOpenPanel: (type: "levels" | "history", row: CompetencyAssignmentJSON) => void;
	onUpdateLevel: (row: CompetencyAssignmentJSON) => void;
};

const CompetenciesTable = ({
	assignments,
	competencies,
	users,
	loading,
	canUpdateCurrentLevel,
	sort,
	onSortChanged,
	onOpenPanel,
	onUpdateLevel,
}: CompetenciesTableProps): React.ReactElement => {
	const userColumns: PYColumnDef<CompetencyAssignmentJSON>[] = users
		? [
				{
					// Built-in employee column → AvatarProfile (avatar + linked name) + loading shimmer.
					colId: "assignee_name",
					headerName: "Assignee",
					type: "employee",
					flex: 1,
					sortable: true,
					valueGetter: (p) => ({
						_id: p.data?.user_id ?? "",
						name: p.data ? users[p.data.user_id]?.name : undefined,
					}),
				},
				{
					colId: "role_name",
					headerName: "Role",
					flex: 1,
					sortable: false,
					cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
						const row = params.data;
						if (isLoadingRow(row)) return <Skeleton className="h-4 w-1/2" />;
						const name = users[row.user_id]?.role_name ?? null;
						return name ? (
							<Text size="paragraph-3" weight="regular">
								<span className="py-line-clamp-1">{name}</span>
							</Text>
						) : null;
					},
				},
			]
		: [];

	const columns: PYColumnDef<CompetencyAssignmentJSON>[] = [
		{
			colId: "competency_name",
			headerName: "Competency",
			flex: 1,
			sortable: true,
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				const entry = competencies[row.competency_id];
				return (
					<div className="flex min-w-0 items-center gap-2">
						<Text size="paragraph-3" weight="regular" dangerouslySetClassName="min-w-0">
							<span className="block py-text-truncate">{entry?.name}</span>
						</Text>
						{entry?.is_company_wide && (
							<Tooltip content="Company Wide Competency">
								<span
									data-testid="company-wide-icon"
									className="flex size-4 shrink-0 items-center justify-center"
								>
									<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
								</span>
							</Tooltip>
						)}
					</div>
				);
			},
		},
		...userColumns,
		{
			colId: "current_level",
			headerName: "Current Level",
			sortable: true,
			width: 160,
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-20" />;
				const name = resolveLevelName(competencies[row.competency_id]?.levels, row.current_level);
				return name ? (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{name}</span>
					</Text>
				) : null;
			},
		},
		{
			colId: "expected_level",
			headerName: "Expected Level",
			sortable: true,
			width: 160,
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-20" />;
				const name = resolveLevelName(competencies[row.competency_id]?.levels, row.expected_level);
				return name ? (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{name}</span>
					</Text>
				) : null;
			},
		},
		{
			colId: "status",
			headerName: "Status",
			// Not sortable — status isn't a supported AssignmentSortKey on the assignments list API.
			sortable: false,
			width: 100,
			cellStyle: { display: "flex", alignItems: "center" },
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-5 w-14 rounded-full" />;
				return row.status === "active" ? (
					<Chip label="Active" color="primary" size={20} />
				) : (
					// An archived ASSIGNMENT means the person is no longer assigned — "Archived"
					// wrongly reads as the competency itself being archived.
					<Chip label="Unassigned" color="neutral" size={20} />
				);
			},
		},
		{
			colId: "actions",
			headerName: "",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: CompetencyAssignmentJSON }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				// Archived rows can't be updated backend-side — offering the action here would just 400.
				const kebabOptions = [
					{ key: "view-levels", label: "View Levels" },
					{ key: "view-history", label: "View History" },
					...(canUpdateCurrentLevel && row.status !== "archived"
						? [{ key: "update-level", label: "Update Current Level" }]
						: []),
				];
				return (
					<DropDownMenu
						options={kebabOptions}
						onSelect={(key) => {
							if (key === "view-levels") onOpenPanel("levels", row);
							else if (key === "view-history") onOpenPanel("history", row);
							else if (key === "update-level") onUpdateLevel(row);
						}}
						placement="bottom-end"
						minWidth={120}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	return (
		<div className="min-w-0 flex-1">
			<Table<CompetencyAssignmentJSON>
				autoHeight
				columnDefs={columns}
				rowData={assignments}
				loading={loading}
				getRowId={(params) => params.data._id}
				rowHeight={48}
				defaultColDef={{
					cellStyle: { display: "flex", alignItems: "center" },
					resizable: false,
					suppressMovable: true,
					sortingOrder: ["asc", "desc"],
				}}
				emptyStateText="No Competencies Found"
				onSortChanged={onSortChanged}
				initialState={{
					sort: {
						sortModel: sort.name ? [{ colId: sort.name, sort: sort.direction }] : [],
					},
				}}
			/>
		</div>
	);
};

export default CompetenciesTable;
